/**
 * 수신 바이트를 흘려보내며 blob으로 모은다.
 * 스크럽 영상은 blob URL이어야 시킹이 매끄럽다 — 스트리밍 소스는 range 요청이
 * 왕복하는 동안 HAVE_METADATA에서 멈춘다.
 */
export async function downloadWithProgress(
  url: string,
  onBytes: (delta: number) => void,
  signal?: AbortSignal,
): Promise<Blob> {
  const response = await fetch(url, { signal })
  if (!response.ok) throw new Error(`asset http ${response.status} ${url}`)

  if (response.body === null) {
    const blob = await response.blob()
    onBytes(blob.size)
    return blob
  }

  const reader = response.body.getReader()
  const chunks: Uint8Array[] = []
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    chunks.push(value)
    onBytes(value.byteLength)
  }
  // 타입을 반드시 옮겨 실어야 한다. WebKit은 blob: URL의 미디어 타입을 Blob
  // 자신에게서 읽고, 빈 타입이면 <video>가 소스를 거절한다 — 영상 두 편을
  // 통째로 버퍼링하는 이유가 바로 그 두 <video>이므로, 여기서 타입을 잃으면
  // 아이폰에서 히어로·엔딩이 아예 뜨지 않는다. body가 없는 위쪽 분기는
  // response.blob()이 같은 일을 이미 해준다.
  const type = response.headers.get('content-type') ?? ''
  return new Blob(chunks as BlobPart[], { type })
}

export class AssetCache {
  private urls = new Map<string, string>()
  private disposed = false
  private listeners = new Set<() => void>()

  get(id: string): string | undefined {
    return this.urls.get(id)
  }

  /** 지금까지 들어온 id → blob URL의 불변 스냅샷. React 상태로 그대로 옮겨 담는다. */
  snapshot(): Record<string, string> {
    return Object.fromEntries(this.urls)
  }

  /**
   * blob이 들어올 때마다 알린다. 캐시는 React 밖의 가변 객체라, 알리지 않으면
   * 늦게 도착한 blob을 아무도 보지 못한 채 화면이 스트리밍 소스에 머문다.
   */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  /**
   * clear() 이후에 걸린 put()은 즉시 URL을 회수하고 저장하지 않는다.
   * 언마운트로 abort()된 다운로드라도 이미 진행 중이던 스트림 읽기는 abort와
   * 무관하게 나중에 resolve될 수 있다 — 그 뒤늦은 continuation이 put()을 불러도
   * 회수되지 않는 blob URL이 남지 않도록, clear() 이후의 캐시는 죽은 상태로
   * 취급한다. 다시 살아나는 건 새 마운트가 reopen()을 부를 때뿐이다.
   */
  put(id: string, blob: Blob): string {
    const url = URL.createObjectURL(blob)
    if (this.disposed) {
      URL.revokeObjectURL(url)
      return url
    }
    const previous = this.urls.get(id)
    if (previous !== undefined) URL.revokeObjectURL(previous)
    this.urls.set(id, url)
    this.notify()
    return url
  }

  private notify(): void {
    for (const listener of this.listeners) listener()
  }

  /**
   * 새 마운트가 이 캐시를 다시 쓰겠다고 선언한다.
   *
   * 폐기는 인스턴스가 아니라 마운트 단위여야 한다. StrictMode는 개발 모드에서
   * effect를 한 번 정리했다가 다시 실행하는데, 폐기가 영구적이면 그 정리 한 번으로
   * 캐시가 영원히 죽어 개발 서버에서는 blob 경로가 단 한 번도 실행되지 않는다 —
   * 배포 전 유일한 수동 QA 채널이 항상 스트리밍 폴백만 보여주게 된다.
   */
  reopen(): void {
    this.disposed = false
  }

  clear(): void {
    for (const url of this.urls.values()) URL.revokeObjectURL(url)
    this.urls.clear()
    this.disposed = true
    // 회수된 URL을 붙들고 있는 소비자가 없도록 비었다는 사실도 알린다.
    this.notify()
  }
}
